import type {
  AdminGeofence, AdminVehicle, BatteryOverheatReportRow, ClusterHealthEvent, ClusterHealthSummary, ConsoleSessionRow,
  ConsoleUser, DarCategoryRow, DarPreview, PipelineStage, ScreenActivityRow, SessionDailyStat, SiteActivityRow,
  TatTarget, TopActiveUserRow, TraceJourney, TracePacket, UserAccess,
} from './types'
import { notifyForbidden } from '../lib/notifyForbidden'

async function adminFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, {
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  })
  const body = await r.json().catch(() => ({}))
  if (!r.ok) {
    const msg = (body as { error?: string }).error ?? `Request failed (${r.status})`
    if (r.status === 403) notifyForbidden(msg)
    throw new Error(msg)
  }
  return body as T
}

export async function fetchAdminStatus(): Promise<{ configured: boolean; topology: boolean }> {
  return adminFetch('/api/admin/status')
}

export async function fetchVehicles(): Promise<AdminVehicle[]> {
  const data = await adminFetch<{ vehicles: AdminVehicle[] }>('/api/admin/vehicles')
  return data.vehicles
}

export async function createVehicle(v: Partial<AdminVehicle>): Promise<void> {
  await adminFetch('/api/admin/vehicles', { method: 'POST', body: JSON.stringify(v) })
}

export async function updateVehicle(id: string, v: Partial<AdminVehicle>): Promise<void> {
  await adminFetch(`/api/admin/vehicles/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(v),
  })
}

export async function deleteVehicle(id: string): Promise<void> {
  await adminFetch(`/api/admin/vehicles/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export async function fetchTatTargets(): Promise<TatTarget[]> {
  const data = await adminFetch<{ targets: TatTarget[] }>('/api/admin/tat-targets')
  return data.targets
}

export async function createTatTarget(t: Partial<TatTarget>): Promise<void> {
  await adminFetch('/api/admin/tat-targets', { method: 'POST', body: JSON.stringify(t) })
}

export async function updateTatTarget(t: TatTarget): Promise<void> {
  await adminFetch('/api/admin/tat-targets', { method: 'PUT', body: JSON.stringify(t) })
}

export async function deleteTatTarget(t: TatTarget): Promise<void> {
  await adminFetch('/api/admin/tat-targets', { method: 'DELETE', body: JSON.stringify(t) })
}

export async function fetchStages(): Promise<{ stages: PipelineStage[]; topics: string[] }> {
  return adminFetch('/api/admin/stages')
}

export async function fetchGeofences(): Promise<{ geofences: AdminGeofence[]; source: string }> {
  return adminFetch('/api/admin/geofences')
}

export async function createGeofence(g: Partial<AdminGeofence>): Promise<void> {
  await adminFetch('/api/admin/geofences', { method: 'POST', body: JSON.stringify(g) })
}

export async function updateGeofence(id: string, g: Partial<AdminGeofence>): Promise<void> {
  await adminFetch(`/api/admin/geofences/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(g),
  })
}

export async function deleteGeofence(id: string): Promise<void> {
  await adminFetch(`/api/admin/geofences/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export async function fetchThresholds(): Promise<{
  overrides: import('./types').ThresholdOverride[]
  knobs: import('./types').KnobRow[]
  locked: string[]
  configured: boolean
}> {
  return adminFetch('/api/config/thresholds')
}

export async function createThreshold(t: {
  knob: string; value: string; scope: string; scope_id?: string; note?: string
}): Promise<{ applies_on: string }> {
  const q = new URLSearchParams({
    knob: t.knob, value: t.value, scope: t.scope,
    scope_id: t.scope_id ?? '', note: t.note ?? '', by: 'supervisor',
  })
  return adminFetch(`/api/config/thresholds?${q}`, { method: 'POST' })
}

export async function fetchSpeedSegments(): Promise<{
  routes: Record<string, import('./types').SpeedSegment[]>
  fences: import('./types').SpeedFence[]
}> {
  const data = await adminFetch<{
    routes: Record<string, import('./types').SpeedSegment[]>
    fences: import('./types').SpeedFence[]
  }>('/api/config/speed-segments')
  return { routes: data.routes, fences: data.fences ?? [] }
}

export async function fetchBoundaryMap(): Promise<import('./types').BoundaryMapping[]> {
  const data = await adminFetch<{ mappings: import('./types').BoundaryMapping[] }>(
    '/api/config/boundary-map')
  return data.mappings
}

export async function upsertBoundaryMapping(m: import('./types').BoundaryMapping): Promise<void> {
  const q = new URLSearchParams({
    event_code: m.event_code, place: m.place, phase: m.phase, edge: m.edge,
    note: m.note ?? '', by: 'supervisor',
  })
  await adminFetch(`/api/config/boundary-map?${q}`, { method: 'POST' })
}

export async function deleteBoundaryMapping(eventCode: string): Promise<void> {
  await adminFetch(`/api/config/boundary-map/${encodeURIComponent(eventCode)}`, {
    method: 'DELETE',
  })
}

// ------------------------------------------------------------------ accounts (super admin)
export async function fetchConsoleUsers(): Promise<ConsoleUser[]> {
  const data = await adminFetch<{ users: ConsoleUser[] }>('/api/auth/users')
  return data.users
}

export async function createConsoleUser(
  username: string, password: string, role: string,
): Promise<ConsoleUser> {
  const data = await adminFetch<{ user: ConsoleUser }>('/api/auth/users', {
    method: 'POST', body: JSON.stringify({ username, password, role }),
  })
  return data.user
}

export async function disableConsoleUser(id: number): Promise<void> {
  await adminFetch(`/api/auth/users/${id}`, { method: 'DELETE' })
}

/** Hard-delete. Distinct from disableConsoleUser (DELETE), which only deactivates.
 *  The account, its department/site grant, and leftover sessions go together. */
export async function deleteConsoleUser(id: number): Promise<void> {
  await adminFetch(`/api/auth/users/${id}/purge`, { method: 'POST' })
}

export async function enableConsoleUser(id: number): Promise<void> {
  await adminFetch(`/api/auth/users/${id}/enable`, { method: 'POST' })
}

export async function updateConsoleUserRole(id: number, role: string): Promise<ConsoleUser> {
  const data = await adminFetch<{ user: ConsoleUser }>(`/api/auth/users/${id}/role`, {
    method: 'POST', body: JSON.stringify({ role }),
  })
  return data.user
}

export async function renameConsoleUser(id: number, username: string): Promise<ConsoleUser> {
  const data = await adminFetch<{ user: ConsoleUser }>(`/api/auth/users/${id}/username`, {
    method: 'POST', body: JSON.stringify({ username }),
  })
  return data.user
}

export async function resetConsoleUserPassword(
  id: number, password: string,
): Promise<{ username: string; reseededNote: string | null }> {
  const data = await adminFetch<{ username: string; reseeded_note?: string | null }>(
    `/api/auth/users/${id}/password`,
    { method: 'POST', body: JSON.stringify({ password }) },
  )
  return { username: data.username, reseededNote: data.reseeded_note ?? null }
}

// ------------------------------------------------------------------ department+site access (super admin)
export async function fetchUserAccess(): Promise<UserAccess[]> {
  const data = await adminFetch<{ access: UserAccess[] }>('/api/auth/access')
  return data.access
}

export async function upsertUserAccess(
  subject: string, department: string, sites: string[],
): Promise<UserAccess> {
  // Returns the server's resolved subject, not just the one typed in -- a bare "kunal.s"
  // becomes "kunal.s@transvolt.in" server-side, and the caller needs that back to show
  // what was actually saved.
  const data = await adminFetch<{ access: UserAccess }>('/api/auth/access', {
    method: 'POST',
    body: JSON.stringify({ subject, department, sites }),
  })
  return data.access
}

export async function deleteUserAccess(subject: string): Promise<void> {
  await adminFetch(`/api/auth/access/${encodeURIComponent(subject)}`, { method: 'DELETE' })
}

// ------------------------------------------------------------------ cluster health (super admin only)
export async function fetchHealthEvents(openOnly: boolean): Promise<ClusterHealthEvent[]> {
  const data = await adminFetch<{ events: ClusterHealthEvent[] }>(
    `/api/admin/health/events?open_only=${openOnly ? 'true' : 'false'}`,
  )
  return data.events
}

export async function fetchHealthSummary(): Promise<ClusterHealthSummary> {
  return adminFetch('/api/admin/health/summary')
}

// ------------------------------------------------------------------ DAR (Daily Alerts Report, super admin only)
export async function fetchDarPreview(): Promise<DarPreview> {
  return adminFetch('/api/admin/dar/preview')
}

export async function fetchDarCategories(): Promise<DarCategoryRow[]> {
  const data = await adminFetch<{ rows: DarCategoryRow[] }>('/api/admin/dar/categories')
  return data.rows
}

export async function toggleDarCategory(site: string, category: string, enabled: boolean): Promise<void> {
  await adminFetch('/api/admin/dar/categories/toggle', {
    method: 'POST',
    body: JSON.stringify({ site, category, enabled }),
  })
}

/** Same fetch-blob-and-save-as dance ReportDownloadCard uses for the other workbooks --
 * not reused directly because this report has no period/date picker at all (always
 * current week + trailing 6 months, server-side) and no preview endpoint yet. */
export async function downloadDarWorkbook(): Promise<void> {
  const res = await fetch('/api/admin/dar/workbook.xlsx')
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: string } | null
    throw new Error(body?.error ?? `${res.status} ${res.statusText}`)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  const disposition = res.headers.get('content-disposition') ?? ''
  const match = /filename="([^"]+)"/.exec(disposition)
  a.download = match?.[1] ?? 'transvolt-dar.xlsx'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// --------------------------------------------- battery-overheat cross-site report (super admin only)
export interface BatteryOverheatReportFilters {
  site?: string
  vehicleId?: string
  fromTs?: string
  toTs?: string
  sort?: string
  dir?: 'asc' | 'desc'
  limit?: number
  offset?: number
}

function _battery_overheat_params(f: BatteryOverheatReportFilters): URLSearchParams {
  const params = new URLSearchParams()
  if (f.site) params.set('site', f.site)
  if (f.vehicleId) params.set('vehicle_id', f.vehicleId)
  if (f.fromTs) params.set('from_ts', f.fromTs)
  if (f.toTs) params.set('to_ts', f.toTs)
  if (f.sort) params.set('sort', f.sort)
  if (f.dir) params.set('dir', f.dir)
  if (f.limit != null) params.set('limit', String(f.limit))
  if (f.offset != null) params.set('offset', String(f.offset))
  return params
}

export async function fetchBatteryOverheatReport(
  f: BatteryOverheatReportFilters = {},
): Promise<{ rows: BatteryOverheatReportRow[]; total: number; truncated: boolean }> {
  const q = _battery_overheat_params(f).toString()
  const data = await adminFetch<{ rows: BatteryOverheatReportRow[]; total: number; truncated?: boolean }>(
    `/api/reports/battery-overheat${q ? `?${q}` : ''}`)
  // truncated means the route's own per-source row-count safety cap was hit -- total
  // (and the page itself) may be missing real matches beyond what the cap could
  // return (adversarial review round 2, HIGH). Narrowing the filter (vehicle number,
  // a tighter date range) is the fix, not re-fetching.
  return { rows: data.rows ?? [], total: data.total ?? 0, truncated: data.truncated ?? false }
}

/** Same fetch-blob-and-save-as dance downloadDarWorkbook uses, with this report's own
 *  filters threaded through so the CSV matches whatever the table is currently showing. */
export async function downloadBatteryOverheatCsv(f: BatteryOverheatReportFilters = {}): Promise<boolean> {
  const q = _battery_overheat_params(f).toString()
  const res = await fetch(`/api/reports/battery-overheat.csv${q ? `?${q}` : ''}`)
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: string } | null
    throw new Error(body?.error ?? `${res.status} ${res.statusText}`)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  const disposition = res.headers.get('content-disposition') ?? ''
  const match = /filename="([^"]+)"/.exec(disposition)
  a.download = match?.[1] ?? 'battery-overheat.csv'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
  return res.headers.get('x-battery-overheat-truncated') === 'true'
}

// ------------------------------------------------------------------ sessions (super admin only)
export async function fetchOnlineSessions(): Promise<ConsoleSessionRow[]> {
  const data = await adminFetch<{ sessions: ConsoleSessionRow[] }>('/api/auth/sessions/online')
  return data.sessions
}

export interface SessionHistoryFilters {
  subject?: string
  channel?: 'web' | 'mobile'
  fromTs?: string
  toTs?: string
  limit?: number
  offset?: number
}

function _session_history_params(f: SessionHistoryFilters): URLSearchParams {
  const params = new URLSearchParams()
  if (f.subject) params.set('subject', f.subject)
  if (f.channel) params.set('channel', f.channel)
  if (f.fromTs) params.set('from_ts', f.fromTs)
  if (f.toTs) params.set('to_ts', f.toTs)
  if (f.limit != null) params.set('limit', String(f.limit))
  if (f.offset != null) params.set('offset', String(f.offset))
  return params
}

export async function fetchSessionHistory(
  f: SessionHistoryFilters = {},
): Promise<{ rows: ConsoleSessionRow[]; total: number }> {
  const q = _session_history_params(f).toString()
  const data = await adminFetch<{ sessions: ConsoleSessionRow[]; total: number }>(
    `/api/auth/sessions/history${q ? `?${q}` : ''}`)
  return { rows: data.sessions ?? [], total: data.total ?? 0 }
}

export async function fetchSessionStats(days: number): Promise<SessionDailyStat[]> {
  const data = await adminFetch<{ days: SessionDailyStat[] }>(`/api/auth/sessions/stats?days=${days}`)
  return data.days
}

export async function fetchTopActiveUsers(days: number, limit = 10): Promise<TopActiveUserRow[]> {
  const data = await adminFetch<{ users: TopActiveUserRow[] }>(
    `/api/auth/sessions/top-users?days=${days}&limit=${limit}`)
  return data.users
}

export async function fetchSessionActivity(
  days: number, limit = 10,
): Promise<{ topScreens: ScreenActivityRow[]; topSites: SiteActivityRow[] }> {
  const data = await adminFetch<{ top_screens: ScreenActivityRow[]; top_sites: SiteActivityRow[] }>(
    `/api/auth/sessions/activity?days=${days}&limit=${limit}`)
  return { topScreens: data.top_screens ?? [], topSites: data.top_sites ?? [] }
}

// ------------------------------------------------------------------ your own account
export async function changeOwnPassword(
  currentPassword: string, newPassword: string,
): Promise<{ reseededNote: string | null }> {
  const data = await adminFetch<{ ok: boolean; reseeded_note: string | null }>(
    '/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    })
  return { reseededNote: data.reseeded_note }
}

export interface TraceSearchInput {
  traceId?: string
  alertId?: string
  tripId?: string
  vehicleId?: string
  start?: string
  end?: string
}

export async function searchTraces(input: TraceSearchInput): Promise<TracePacket[]> {
  const q = new URLSearchParams()
  if (input.traceId) q.set('trace_id', input.traceId.trim())
  if (input.alertId) q.set('alert_id', input.alertId.trim())
  if (input.tripId) q.set('trip_id', input.tripId.trim())
  if (input.vehicleId) q.set('vehicle_id', input.vehicleId.trim())
  if (input.start) q.set('start', input.start)
  if (input.end) q.set('end', input.end)
  const data = await adminFetch<{ packets: TracePacket[] }>(`/api/admin/traces/search?${q}`)
  return data.packets
}

export async function fetchTraceJourney(traceId: string): Promise<TraceJourney> {
  return adminFetch(`/api/admin/traces/${encodeURIComponent(traceId)}`)
}

export function grafanaExplorePath(traceId: string): string {
  if (!traceId) return '/api/admin/grafana/'
  const left = {
    datasource: 'Loki',
    queries: [{
      refId: 'A',
      expr: `{job="fleet"} | json | trace_id="${traceId}"`,
    }],
  }
  return `/api/admin/grafana/explore?orgId=1&left=${encodeURIComponent(JSON.stringify(left))}`
}
